// Owner-only: add family members and grant the equipment-manager permission.
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const EMPTY = { name: '', email: '', phone: '', role: 'hand', can_manage_equipment: false };

export default function UserManager() {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    try { setUsers((await api.get('/api/admin/users')).users); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function addUser(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api.post('/api/admin/users', form);
      setForm(EMPTY);
      setAdding(false);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function toggleManager(u) {
    setErr('');
    try {
      await api.patch(`/api/admin/users/${u.id}`, { can_manage_equipment: !u.can_manage_equipment });
      await load();
    } catch (e) { setErr(e.message); }
  }

  return (
    <section className="card space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium text-olive-900">People</h2>
          <p className="text-sm text-ink-700/70">
            Everyone signs in with a link emailed to the address below. <strong>Equipment manager</strong> lets
            a hand add machines, upload manuals, and set up service schedules — without seeing purchase prices or the books.
          </p>
        </div>
        {!adding && <button onClick={() => setAdding(true)} className="btn-primary text-sm whitespace-nowrap">+ Add person</button>}
      </div>

      {err && <div className="text-rust-700 text-sm">{err}</div>}

      {adding && (
        <form onSubmit={addUser} className="bg-cream-100 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="label">Name</label>
              <input className="input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Antonio King" />
            </div>
            <div>
              <label className="label">Email (for sign-in links)</label>
              <input className="input" type="email" required value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div>
              <label className="label">Mobile phone (for service alerts)</label>
              <input className="input" type="tel" required value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="662-555-0123" />
            </div>
            <div>
              <label className="label">Role</label>
              <select className="input" value={form.role} onChange={e => set('role', e.target.value)}>
                <option value="hand">Hand — day-to-day work, no financials</option>
                <option value="owner">Owner — sees everything</option>
              </select>
            </div>
          </div>
          {form.role === 'hand' && (
            <label className="flex items-center gap-2 text-sm text-ink-900">
              <input type="checkbox" checked={form.can_manage_equipment} onChange={e => set('can_manage_equipment', e.target.checked)} />
              Equipment manager
            </label>
          )}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setAdding(false); setForm(EMPTY); setErr(''); }} className="btn-ghost text-sm">Cancel</button>
            <button className="btn-primary text-sm" disabled={busy}>{busy ? 'Adding…' : 'Add person'}</button>
          </div>
        </form>
      )}

      {users == null ? (
        <p className="text-sm text-ink-700/60 italic">Loading…</p>
      ) : (
        <ul className="divide-y divide-olive-100/60">
          {users.map(u => (
            <li key={u.id} className="py-2 flex items-center gap-3 flex-wrap text-sm">
              <div className="flex-1 min-w-[12rem]">
                <div className="text-ink-900 font-medium">
                  {u.name} <span className="pill bg-cream-100 ring-1 ring-olive-100 text-ink-700 ml-1">{u.role}</span>
                </div>
                <div className="text-xs text-ink-700/60 break-all">{u.email || 'no email — cannot sign in'} · {u.phone}</div>
              </div>
              {u.role === 'owner' ? (
                <span className="text-xs text-ink-700/50">manages equipment (owner)</span>
              ) : (
                <label className="flex items-center gap-2 text-xs text-ink-900 cursor-pointer">
                  <input type="checkbox" checked={!!u.can_manage_equipment} onChange={() => toggleManager(u)} />
                  Equipment manager
                </label>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
